import { useEffect, useRef } from 'react';

import { bridgeAtom, isOpenChatAtom, useValue, viewedProductsAtom, widgetPopupsStatesAtom } from '@/store';

import { PopupState, productsTooltipConfig, WidgetPopupType } from '@/modules/models';

import { usePausableTimeout } from './usePausableTimeout';

const DEFAULT_VIEWED_PRODUCTS_COUNT = 3;
const DEFAULT_DELAY = 4000;
const DEFAULT_DURATION = 5000;

interface UseViewedProductsPopupProps {
  isDisabled: boolean;
  activePopup: WidgetPopupType | null;
  isWidgetHidden: boolean;
  isHover: boolean;
  setPopupState: (popupType: WidgetPopupType, state: PopupState) => void;
  startPopupWithDelay: (popupType: WidgetPopupType, delay: number) => void;
  hidePopup: () => void;
}

export const useViewedProductsPopup = ({
  activePopup,
  isWidgetHidden,
  isHover,
  setPopupState,
  startPopupWithDelay,
  hidePopup,
}: UseViewedProductsPopupProps) => {
  const startedRef = useRef(false);

  const isOpenChat = useValue(isOpenChatAtom);
  const viewedProducts = useValue(viewedProductsAtom);
  const { widget } = useValue(bridgeAtom);
  const widgetPopupsStates = useValue(widgetPopupsStatesAtom);

  const { VIEWED_PRODUCTS, INIT_WITH_CLOSE_BTN } = widgetPopupsStates;
  const { productsTooltip } = widget || {};
  const viewedProductsCount = viewedProducts.length;
  const productsNumber = (productsTooltip as productsTooltipConfig)?.products ?? DEFAULT_VIEWED_PRODUCTS_COUNT;
  const delay = (productsTooltip as productsTooltipConfig)?.delay || DEFAULT_DELAY;
  const duration = (productsTooltip as productsTooltipConfig)?.duration || DEFAULT_DURATION;
  const pauseOnHover = Boolean((productsTooltip as productsTooltipConfig)?.pauseOnHover);

  const { start } = usePausableTimeout(isHover, pauseOnHover, hidePopup);

  useEffect(() => {
    if (
      isOpenChat ||
      activePopup !== null ||
      !productsTooltip ||
      isWidgetHidden ||
      VIEWED_PRODUCTS !== PopupState.IDLE ||
      INIT_WITH_CLOSE_BTN !== PopupState.CLOSED ||
      viewedProductsCount !== productsNumber
    ) {
      return;
    }

    setPopupState(WidgetPopupType.VIEWED_PRODUCTS, PopupState.STARTED);
  }, [
    INIT_WITH_CLOSE_BTN,
    isOpenChat,
    activePopup,
    viewedProductsCount,
    productsTooltip,
    productsNumber,
    isWidgetHidden,
    VIEWED_PRODUCTS,
    setPopupState,
  ]);

  useEffect(() => {
    if (VIEWED_PRODUCTS !== PopupState.STARTED || startedRef.current) {
      return;
    }

    startedRef.current = true;
    startPopupWithDelay(WidgetPopupType.VIEWED_PRODUCTS, delay);
  }, [VIEWED_PRODUCTS, startPopupWithDelay, delay]);

  useEffect(() => {
    if (VIEWED_PRODUCTS === PopupState.VISIBLE) {
      start(duration);
      setPopupState(WidgetPopupType.VIEWED_PRODUCTS, PopupState.SEEN);
    }
  }, [VIEWED_PRODUCTS, start, duration, setPopupState]);

  return null;
};
